import { dateTimeFormat, formatFullName, formatToRunningCase } from "src/utils";
import styles from "./licenceReportsContent.module.scss";
import { EMPTY_STRING, reportTypeMapping, roleMapping } from "src/components/constant/constant";
import { bgColorbyName, roleColors } from "src/variable/randomColor";
import { Link } from "react-router-dom";
import { Avatar } from "@mui/material";
import { EmptyCell, cellClasses } from "src/components/UI/transactionCells";
import { ReportsContent } from "../reportsContent/reportsContentSlice";

const columns = [
  {
    title: "Id",
    dataIndex: "object_id",
    key: "object_id"
  },
  {
    title: "Organisation",
    dataIndex: "data",
    key: "organisation_id",
    render: (data: { organisation_id: string; organisation_name: string }) => (
      <Link to={`/account/${data.organisation_id}`} className={styles.tableMember}>
        <div className={styles.tableMemberImg}>
          <Avatar
            id="av"
            aria-label="Member"
            style={{ backgroundColor: bgColorbyName(data.organisation_name ?? EMPTY_STRING) }}
          >
            {data.organisation_name?.charAt(0).toLocaleUpperCase()}
          </Avatar>
        </div>
        <div className={styles.tableMemberDetail}>
          <div className={styles.tableMemberName}>{data.organisation_name}</div>
        </div>
      </Link>
    )
  },
  {
    title: "Date",
    dataIndex: "report_date",
    key: "report_date",
    render: (report_date: string) => <div>{dateTimeFormat(report_date).date}</div>
  },
  {
    title: "Type",
    dataIndex: "report_type",
    key: "report_type",
    render: (report_type: string) => <div>{reportTypeMapping[report_type] || report_type}</div>
  },
  {
    title: "Licency",
    dataIndex: "data",
    key: "user",
    render: (
      data: {
        user: { user_id: string; attributes: { first_name: string; last_name: string }; email: string };
        group: { group_id: string; name: string; group_type: string };
      },
      record: ReportsContent
    ) => {
      let link = EMPTY_STRING;
      let name = EMPTY_STRING;
      let email = EMPTY_STRING;

      if (record.object_type === "USER") {
        link = `/users/${data.user.user_id}`;
        name = formatFullName(data.user.attributes?.first_name, data.user.attributes?.last_name);
        email = data.user.email;
      } else if (record.object_type === "GROUP") {
        link = `/groups/${data.group.group_id}`;
        name = data.group.name;
        email = data.group.group_type;
      }
      return (
        <Link to={link} className={styles.tableMember}>
          <div className={styles.tableMemberImg}>
            <Avatar id="av" aria-label="Member" style={{ backgroundColor: bgColorbyName(name ?? EMPTY_STRING) }}>
              {name?.charAt(0).toLocaleUpperCase()}
            </Avatar>
          </div>
          <div className={styles.tableMemberDetail}>
            <div className={styles.tableMemberName}>{name}</div>
            <div className={styles.tableMemberEmail}>{email}</div>
          </div>
        </Link>
      );
    }
  },
  {
    title: "Role",
    dataIndex: "data",
    key: "role",
    render: (data: { user?: { role?: string } }, record: ReportsContent) => {
      if (record.object_type !== "USER") return <EmptyCell />;

      return (
        <span
          className={cellClasses.statusPill}
          style={{
            backgroundColor: `${roleColors[data.user?.role || "NOROLE"]}14`,
            borderColor: `${roleColors[data.user?.role || "NOROLE"]}40`,
            color: roleColors[data.user?.role || "NOROLE"]
          }}
        >
          {roleMapping[data.user?.role || "NOROLE"]}
        </span>
      );
    }
  },
  {
    title: "Licency Type",
    dataIndex: "object_type",
    key: "object_type",
    render: (object_type: string) => (
      <div
        style={{
          color: object_type === "USER" ? "#007bff" : "#3b3b6b",
          backgroundColor: object_type === "USER" ? "#007bff0d" : "#3b3b6b0d",
          border: object_type === "USER" ? "1px solid #007bff" : "1px solid #3b3b6b",
          padding: "2px 4px",
          borderRadius: "8px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center"
        }}
      >
        {object_type?.slice(0, 1).toUpperCase() + object_type?.slice(1).toLowerCase()}
      </div>
    )
  },
  {
    title: "Licence Tag",
    dataIndex: "data",
    key: "licence_tag",
    render: (data: { licence_tag: string }) => <div>{formatToRunningCase(data.licence_tag) ?? "---"}</div>
  },
  {
    title: "Card Count",
    dataIndex: "data",
    key: "wallet_count",
    render: (data: { attached_wallet_ids: string[] }) => <div>{data?.attached_wallet_ids?.length}</div>
  },
  {
    title: "Card Count",
    dataIndex: "data",
    key: "card_count",
    render: (data: { attached_cards_ids: string[] }) => <div>{data?.attached_cards_ids?.length}</div>
  }
];

export const convertToCSV = (data: any[]) => {
  const headers = [
    "Report Content Id",
    "Organisation_ID",
    "Organisation_Name",
    "Date",
    "Type",
    "Licency ID",
    "Licency Name",
    "Licency Email",
    "Role",
    "Licency Type",
    "Wallet Count",
    "Card Count"
  ];

  const rows = data.map((item) => {
    const dataObj = item.data;

    return [
      item.object_id,
      dataObj.organisation_id,
      dataObj.organisation_name,
      item.report_date,
      item.report_type,
      item.object_type === "USER" ? dataObj.user.user_id : dataObj.group.group_id,
      item.object_type === "USER"
        ? formatFullName(dataObj.user.attributes?.first_name, dataObj.user.attributes?.last_name)
        : dataObj.group.name,
      item.object_type === "USER" ? dataObj.user.email : dataObj.group.group_type,
      item.object_type === "USER" ? roleMapping[dataObj.user?.role || "NOROLE"] : EMPTY_STRING,
      item.object_type,
      dataObj.attached_wallet_ids.length,
      dataObj.attached_cards_ids.length
    ];
  });

  const csvContent = [
    headers.join(","),
    ...rows.map((row) => row.map((cell) => `"${String(cell).replace(/"/g, "EMPTY_STRING")}"`).join(","))
  ].join("\n");

  return csvContent;
};

export default columns;
